<template>
  <div>
    <header class="wsp-toolbar">
      <div class="wsp-toolbar__title">
        <span class="wsp-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Timeline</h1>
          <p>Company milestones shown on the about page</p>
        </div>
      </div>
      <div class="wsp-toolbar__actions">
        <div class="wsp-search">
          <span class="wsp-search__icon">🔍</span>
          <input v-model.trim="search" type="text" placeholder="Search milestones…" />
        </div>
        <button class="wsp-btn-refresh" :class="{ 'is-loading': loading }" :disabled="loading" @click="fetch">
          <svg class="wsp-btn-refresh__icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ loading ? 'Refreshing…' : 'Refresh' }}</span>
        </button>
        <button class="wsp-btn-primary" @click="openCreate">+ Add Milestone</button>
      </div>
    </header>

    <CAlert v-if="error" color="danger" show>{{ error }}</CAlert>

    <section class="wsp-card">
      <header class="wsp-card__head">
        <h2>All Milestones</h2>
        <span class="wsp-card__count">{{ filtered.length }} milestone(s)</span>
      </header>

      <div v-if="loading" class="wsp-loading"><CSpinner size="sm" /> Loading…</div>
      <div v-else-if="!filtered.length" class="wsp-empty">
        <div class="wsp-empty__title">No milestones yet</div>
        <div>Add your first timeline entry.</div>
      </div>
      <div v-else>
        <table class="wsp-table">
          <thead>
            <tr>
              <th style="width:80px;">Year</th>
              <th>Title</th>
              <th>Description</th>
              <th>Image</th>
              <th>Position</th>
              <th class="wsp-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in filtered" :key="row.id">
              <td><span class="ws-year">{{ row.year }}</span></td>
              <td><strong>{{ row.title }}</strong></td>
              <td style="max-width:380px;color:#4b5563;font-size:0.78rem;">
                <div style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                  {{ row.description }}
                </div>
              </td>
              <td>
                <img v-if="row.image" :src="row.image" class="wsp-thumb" alt="" />
                <span v-else style="color:#9ca3af;font-size:0.75rem;">—</span>
              </td>
              <td>{{ row.position == null ? '—' : row.position }}</td>
              <td class="wsp-actions">
                <button class="wsp-icon-btn" @click="openEdit(row)" title="Edit">✎</button>
                <button class="wsp-icon-btn wsp-icon-btn--danger" @click="confirmDelete(row)" title="Delete">🗑</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <div v-if="modalOpen" class="wsp-modal-backdrop" @click.self="closeModal">
      <div class="wsp-modal">
        <header class="wsp-modal__head">
          <h3>{{ form.id ? 'Edit Milestone' : 'Add Milestone' }}</h3>
          <button class="wsp-modal__close" @click="closeModal">×</button>
        </header>
        <div class="wsp-modal__body">
          <div class="wsp-form-grid">
            <div class="wsp-field">
              <label>Year <span style="color:#b91c1c">*</span></label>
              <input v-model="form.year" type="text" maxlength="10" placeholder="1985" />
              <span v-if="errors.year" class="wsp-field__error">{{ errors.year }}</span>
            </div>
            <div class="wsp-field">
              <label>Position</label>
              <input v-model.number="form.position" type="number" />
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Title <span style="color:#b91c1c">*</span></label>
              <input v-model="form.title" type="text" />
              <span v-if="errors.title" class="wsp-field__error">{{ errors.title }}</span>
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Description <span style="color:#b91c1c">*</span></label>
              <textarea v-model="form.description" rows="4"></textarea>
              <span v-if="errors.description" class="wsp-field__error">{{ errors.description }}</span>
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Image</label>

              <div class="ms-img-row">
                <div class="ms-preview" :class="{ 'ms-preview--empty': !form.image }">
                  <img v-if="form.image" :src="form.image" alt="" />
                  <span v-else>No image</span>
                </div>

                <div class="ms-img-actions">
                  <div
                    class="img-drop"
                    :class="{ 'img-drop--over': dragOver, 'img-drop--busy': uploading }"
                    @dragover.prevent="dragOver = true"
                    @dragleave.prevent="dragOver = false"
                    @drop.prevent="onDrop($event)"
                    @click="$refs.imgInput.click()"
                  >
                    <input
                      ref="imgInput"
                      type="file"
                      accept="image/*"
                      hidden
                      @change="onPickFile"
                    />
                    <div v-if="uploading" class="img-drop__hint">
                      <CSpinner size="sm" /> Uploading…
                    </div>
                    <div v-else class="img-drop__hint">
                      <strong>{{ form.image ? 'Replace image' : 'Click or drop image' }}</strong>
                      <span>JPG, PNG, WEBP · up to 10 MB</span>
                    </div>
                  </div>
                  <button
                    v-if="form.image"
                    type="button"
                    class="ms-img-remove"
                    @click="form.image = ''"
                  >Remove</button>
                </div>
              </div>
              <span v-if="errors.upload" class="wsp-field__error">{{ errors.upload }}</span>

              <details class="img-url-fallback">
                <summary>Or paste a URL</summary>
                <input v-model="form.image" type="text" placeholder="https://…" />
              </details>
            </div>
          </div>
        </div>
        <footer class="wsp-modal__foot">
          <button class="wsp-btn-ghost" :disabled="saving" @click="closeModal">Cancel</button>
          <button class="wsp-btn-primary" :disabled="saving" @click="save">
            {{ saving ? 'Saving…' : (form.id ? 'Save changes' : 'Create milestone') }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import websitesApi from '@/api/websites'

const empty = () => ({ id: null, year: '', title: '', description: '', image: '', position: null })

export default {
  name: 'WebsitesTimeline',
  data () {
    return {
      rows: [], loading: false, error: '',
      search: '',
      modalOpen: false, saving: false,
      form: empty(), errors: {},
      dragOver: false, uploading: false,
    }
  },
  computed: {
    filtered () {
      const q = this.search.toLowerCase()
      if (!q) return this.rows
      return this.rows.filter(r =>
        (r.year || '').toLowerCase().includes(q) ||
        (r.title || '').toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q)
      )
    },
  },
  mounted () { this.fetch() },
  methods: {
    async fetch () {
      this.loading = true; this.error = ''
      try { this.rows = await websitesApi.timelineList() }
      catch (e) { this.error = this.extractError(e) }
      finally { this.loading = false }
    },
    openCreate () {
      this.form = empty()
      this.errors = {}
      this.resetUploader()
      this.modalOpen = true
    },
    openEdit (row) {
      this.form = {
        id: row.id,
        year: row.year || '',
        title: row.title || '',
        description: row.description || '',
        image: row.image || '',
        position: row.position == null ? null : Number(row.position),
      }
      this.errors = {}
      this.resetUploader()
      this.modalOpen = true
    },
    resetUploader () { this.dragOver = false; this.uploading = false },
    onPickFile (e) {
      const file = (e.target.files || [])[0]
      e.target.value = ''
      if (file) this.uploadFile(file)
    },
    onDrop (e) {
      this.dragOver = false
      const file = Array.from(e.dataTransfer.files || []).find(f => f.type.startsWith('image/'))
      if (file) this.uploadFile(file)
    },
    async uploadFile (file) {
      if (this.uploading) return
      this.errors = { ...this.errors, upload: '' }
      this.uploading = true
      try {
        const data = await websitesApi.uploadImage(file, 'websites/timeline')
        if (data && data.url) this.form.image = data.url
      } catch (err) {
        this.errors = { ...this.errors, upload: this.extractError(err) }
      } finally {
        this.uploading = false
      }
    },
    closeModal () { if (!this.saving) this.modalOpen = false },
    async save () {
      this.errors = {}
      if (!this.form.year.trim())        this.errors.year        = 'Required'
      if (!this.form.title.trim())       this.errors.title       = 'Required'
      if (!this.form.description.trim()) this.errors.description = 'Required'
      if (Object.keys(this.errors).length) return
      this.saving = true
      try {
        const payload = {
          year: this.form.year,
          title: this.form.title,
          description: this.form.description,
          image: this.form.image || null,
          position: this.form.position == null || this.form.position === '' ? null : Number(this.form.position),
        }
        if (this.form.id) await websitesApi.timelineUpdate(this.form.id, payload)
        else              await websitesApi.timelineCreate(payload)
        this.modalOpen = false
        await this.fetch()
      } catch (e) { this.error = this.extractError(e) }
      finally { this.saving = false }
    },
    async confirmDelete (row) {
      if (!confirm(`Delete milestone "${row.title}"?`)) return
      try { await websitesApi.timelineDelete(row.id); await this.fetch() }
      catch (e) { this.error = this.extractError(e) }
    },
    extractError (e) {
      return (e && e.response && e.response.data && (e.response.data.error || e.response.data.message))
        || (e && e.message) || 'Request failed'
    },
  },
}
</script>

<style scoped>
.ws-year {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 8px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #fff;
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 0.04em;
}

.ms-img-row {
  display: flex;
  gap: 14px;
  align-items: stretch;
}
.ms-preview {
  flex: 0 0 160px;
  width: 160px;
  height: 110px;
  border-radius: 10px;
  border: 1px solid #e5e9f2;
  background: #f3f4f8;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 0.74rem;
}
.ms-preview img { width: 100%; height: 100%; object-fit: cover; display: block; }
.ms-preview--empty { border-style: dashed; }
.ms-img-actions {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.img-drop {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border: 1.5px dashed #c5cbd6;
  border-radius: 10px;
  background: #fbfcfe;
  padding: 14px;
  cursor: pointer;
  transition: all 0.18s ease;
  min-height: 70px;
}
.img-drop:hover { border-color: #16a07a; background: #f0fbf6; }
.img-drop--over { border-color: #16a07a; background: #e6f7ef; }
.img-drop--busy { cursor: progress; opacity: 0.85; }
.img-drop__hint {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.82rem;
  color: #4b5563;
}
.img-drop__hint strong { color: #1f2a44; font-weight: 600; }
.img-drop__hint span { font-size: 0.72rem; color: #6b7280; }

.ms-img-remove {
  align-self: flex-start;
  background: transparent;
  border: 1px solid #e5e9f2;
  color: #b91c1c;
  font-size: 0.74rem;
  padding: 4px 10px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.ms-img-remove:hover { background: #fef2f2; border-color: #fecaca; }

.img-url-fallback { margin-top: 10px; font-size: 0.78rem; }
.img-url-fallback summary {
  cursor: pointer;
  color: #6b7280;
  padding: 4px 0;
  user-select: none;
}
.img-url-fallback summary:hover { color: #0e5b46; }
.img-url-fallback input { margin-top: 6px; }
</style>
